import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { FormBasedFlowBuilder } from "@/components/flow-builder/FormBasedFlowBuilder";
import { PageSkeleton } from "@/components/page-skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useFlowGraph, useSaveFlowGraph, useCreateFlow, useCategories} from "@/hooks/api/flows";
import { apiErrorMessage } from "@/lib/api";
import type { FlowNode, FlowEdge } from "@/types/api";

/**
 * Flow Wizard Page
 *
 * Simplified UI for creating call scripts using the form-based wizard.
 * Accessible at:
 * - /flows/create?wizard=true (new flow)
 * - /flows/{id}/wizard (edit existing)
 */

function FlowWizardInner() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [flowTitle, setFlowTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");

  const { data: flow, isLoading: flowLoading } = useFlowGraph(id);
  const { data: categoriesData } = useCategories();
  const createFlow = useCreateFlow();
  const saveFlowGraph = useSaveFlowGraph(id);

  // Pre-fill if editing
  useEffect(() => {
    if (flow && id) {
      setFlowTitle(flow.flow.title);
      setCategoryId(flow.flow.category.id);
    }
  }, [flow, id]);

  const handleSave = async (flowData: { nodes: FlowNode[]; edges: FlowEdge[] }) => {
    if (!flowTitle.trim()) {
      toast.error("Please enter a script name");
      return;
    }

    if (!categoryId) {
      toast.error("Please select a category");
      return;
    }

    try {
      const payload = {
        nodes: flowData.nodes.map((n) => ({
          client_id: n.id,
          node_type: n.node_type,
          title: n.title,
          title_es: n.title_es,
          spiel_text: n.spiel_text,
          spiel_text_es: n.spiel_text_es,
          internal_note: n.internal_note,
          internal_note_es: n.internal_note_es,
          condition_text: n.condition_text,
          condition_text_es: n.condition_text_es,
          builder_step_type: n.builder_step_type,
          position_x: n.position_x,
          position_y: n.position_y,
        })),
        edges: flowData.edges.map((e) => ({
          source_node: e.source_node,
          target_node: e.target_node,
          label: e.label,
          label_es: e.label_es,
          sort_order: e.sort_order,
        })),
      };

      if (id) {
        // Edit existing flow
        await saveFlowGraph.mutateAsync(payload);
        toast.success("Script updated!");
        navigate("/flows");
      } else {
        // Create new flow
        const newFlow = await createFlow.mutateAsync({
          title: flowTitle,
          title_es: flowTitle, // Use English for now
          category_id: categoryId,
        });

        // Save the graph for the new flow
        if (newFlow.id) {
          toast.success("Script created!");
          navigate(`/wizard-flows/${newFlow.id}`);
        }
      }
    } catch (error) {
      const msg = apiErrorMessage(error, "Failed to save script");
      toast.error(msg);
    }
  };

  if (flowLoading) {
    return <PageSkeleton />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/flows" aria-label="Back to scripts">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-semibold">
              {id ? "Edit Workflow" : "Create New Workflow"}
            </h1>
            <p className="text-sm text-slate-600">
              Build from scratch with flexible form-based steps
            </p>
          </div>
        </div>
      </div>

      {/* Basic Info Card */}
      <Card className="p-4 mb-4">
        <h2 className="font-semibold mb-3">Workflow Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Workflow Name</Label>
            <Input
              value={flowTitle}
              onChange={(e) => setFlowTitle(e.target.value)}
              placeholder="e.g., Standard Support Call, Customer Onboarding"
            />
          </div>

          <div>
            <Label className="text-xs">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categoriesData?.results.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Main Builder */}
      <FormBasedFlowBuilder
        onSave={handleSave}
        isSaving={saveFlowGraph.isPending || createFlow.isPending}
      />
    </div>
  );
}

export default function FlowWizardPage() {
  return (
    <AppShell>
      <FlowWizardInner />
    </AppShell>
  );
}
